import { sha256Hex } from "./sha256";

/** A paragraph, optionally emphasized, or a bulleted list. */
export type AgreementBlock =
  | { type: "paragraph"; text: string; strong?: boolean }
  | { type: "list"; items: string[] };

export interface AgreementSection {
  heading: string;
  blocks: AgreementBlock[];
}

export type AgreementKind = "user" | "boatLoan";

/**
 * The exact content someone signs. The browser renders it and the server stores it;
 * both derive the same canonical text and fingerprint from this structure.
 */
export interface AgreementDocument {
  kind: AgreementKind;
  version: string;
  title: string;
  /** Prominent notice shown above everything else. */
  notice: string;
  /** Labelled particulars, e.g. the boat and dates of a loan. */
  facts: { label: string; value: string }[];
  sections: AgreementSection[];
}

/** The canonical plain text of an agreement: what is stored, emailed, and fingerprinted. */
export const agreementText = (doc: AgreementDocument): string => {
  const lines: string[] = [
    doc.title,
    `Version: ${doc.version}`,
    "",
    doc.notice,
  ];
  if (doc.facts.length > 0) {
    lines.push("", ...doc.facts.map((f) => `${f.label}: ${f.value}`));
  }
  for (const section of doc.sections) {
    lines.push("", section.heading);
    for (const block of section.blocks) {
      if (block.type === "paragraph") {
        lines.push(block.text);
      } else {
        lines.push(...block.items.map((item) => `- ${item}`));
      }
    }
  }
  return `${lines.join("\n")}\n`;
};

/** SHA-256 fingerprint of the canonical text, recorded with every signature. */
export const agreementFingerprint = (doc: AgreementDocument): string =>
  sha256Hex(agreementText(doc));

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** "October 2, 2026" from an ISO date, independent of locale so the text never varies. */
export const formatAgreementDate = (isoDate: string): string => {
  const [year, month, day] = isoDate.split("-").map(Number);
  return `${MONTHS[(month ?? 1) - 1] ?? ""} ${day ?? ""}, ${year ?? ""}`;
};
